import { useMemo, useState, useEffect } from "react";
import { App } from "antd";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { InferType } from "yup";
import { DeleteOutlined, EditOutlined, SaveOutlined } from "@ant-design/icons";
import { TFunction } from "i18next/typescript/t";

import {
  useGetCountriesQuery,
  useGetUserByIdQuery,
  useUpdateUserMutation,
} from "@/redux/service.js";
import { convertToFormData, handleErrors } from "@/utilities/forms";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.js";
import UserInfoHeader from "@/components_v2/Header/UserInfoHeader.js";
import {
  HookFormBaseDropdown,
  HookFormBaseUpload,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields.js";
import UserAvatar from "@/components_v2/Avatar/UserAvatar";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper.js";
import { Honorifics, OrganizationRoles } from "@/utilities/constants.jsx";
import { getDefaultValuesForForm } from "@/utilities/forms";
import { Option } from "@/components_v2/FormFields/Dropdown/types";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useNavigationBlocker } from "@/hooks/useNavigationBlocker";
import {
  CustomAvatarUploadProps,
  UserFormValues,
} from "@/views/profiles/types/types";

import "./styles.css";

const supportedFileFormats = [
  ".jpeg",
  ".jpg",
  ".png",
  ".gif",
  ".bmp",
  ".webp",
  ".svg+xml",
];

const defaultValues = {
  firstName: "",
  lastName: "",
  profilePicture: null,
  profile: {
    honorific: "",
    jobTitle: "",
    department: "",
    city: "",
    country: "",
  },
};

const getProfileSchema = (t: TFunction) =>
  yup.object().shape({
    firstName: yup
      .string()
      .typeError(t("profiles.notValid"))
      .required(t("profiles.required")),
    lastName: yup
      .string()
      .typeError(t("profiles.notValid"))
      .required(t("profiles.required")),
    profilePicture: yup
      .mixed<File>()
      .test(
        "fileType",
        t("profiles.profilePictureFormatInvalid", {
          supportedFormats: supportedFileFormats
            .map((format) => format.replace(".", ""))
            .join(", "),
        }),
        (value) => {
          if (!(value instanceof File)) {
            return true;
          }
          const extension = value.name.split(".").pop();
          return supportedFileFormats.includes(`.${extension}`);
        },
      )
      .nullable()
      .default(null),
    profile: yup.object().shape({
      honorific: yup.string().typeError(t("profiles.notValid")),
      jobTitle: yup
        .string()
        .typeError(t("profiles.notValid"))
        .required(t("profiles.required")),
      department: yup
        .string()
        .typeError(t("profiles.notValid"))
        .required(t("profiles.required")),
      city: yup
        .string()
        .typeError(t("profiles.notValid"))
        .required(t("profiles.required")),
      country: yup
        .string()
        .typeError(t("profiles.notValid"))
        .required(t("profiles.required")),
    }),
  });

const CustomAvatarUpload = ({
  form,
  avatarUrl,
  setAvatarUrl,
}: CustomAvatarUploadProps) => {
  const { t } = useTranslation();

  const handleRemoveImage = () => {
    form.setValue("profilePicture", null);
    setAvatarUrl(null);
  };

  return (
    <div className="avatar-upload-wrapper">
      <div
        className={`avatar-upload-circle ${avatarUrl && "hide-overlay-elements"}`}
        style={{ backgroundImage: avatarUrl ? `url(${avatarUrl})` : "none" }}
      >
        <HookFormBaseUpload
          name="profilePicture"
          control={form.control}
          shouldShowValues={false}
          className="avatar-upload-component"
        />
      </div>

      {avatarUrl && (
        <BaseButton
          variant="link"
          leadingIcon={<DeleteOutlined />}
          onClick={handleRemoveImage}
        >
          {t("buttons.removeImage")}
        </BaseButton>
      )}
    </div>
  );
};

const UserForm = ({ userId }: { userId: string }) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();

  const { data: user, isLoading: isUserLoading } = useGetUserByIdQuery(userId);
  const { data: countriesData } = useGetCountriesQuery();
  const [updateUser] = useUpdateUserMutation();
  const [isEdit, setIsEdit] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const profileSchema = getProfileSchema(t);
  const form = useForm<UserFormValues>({
    resolver: yupResolver(profileSchema),
    mode: "onChange",
    defaultValues,
  });
  useNavigationBlocker(form.formState.isDirty, `user-form-${userId}`);

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (values.profilePicture instanceof File) {
        setAvatarUrl(URL.createObjectURL(values.profilePicture as File));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      const formValues = getDefaultValuesForForm(defaultValues, user);
      form.reset(formValues);

      if (user.profilePicture) {
        setAvatarUrl(user.profilePicture);
      }
    }
  }, [user, form, user?.profilePicture]);

  const onSubmit = (values: InferType<typeof profileSchema>) => {
    if (!user) return;

    let formData;
    if (values.profilePicture instanceof File) {
      formData = convertToFormData(values);
    } else {
      if (values.profilePicture !== null) {
        // @ts-expect-error delete profile picture if not File
        delete values?.profilePicture;
      }
      formData = values;
    }

    updateUser({ id: user.id, data: formData })
      .unwrap()
      .then(() => {
        notification.success({ message: t("profiles.profileUpdated") });
        setIsEdit(false);
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  const handleCancel = () => {
    setIsEdit(false);
    form.reset();
  };

  const countries = useMemo(() => {
    if (!countriesData) return [];
    return Object.keys(countriesData).reduce<Option[]>((acc, countryCode) => {
      acc.push({
        value: countryCode,
        label: countriesData[countryCode] as string,
      });
      return acc;
    }, []);
  }, [countriesData]);

  if (isUserLoading) {
    return <BaseSpinner size="large" />;
  }

  return (
    user && (
      <UserInfoHeader
        avatar={
          isEdit ? (
            <CustomAvatarUpload
              form={form}
              avatarUrl={avatarUrl}
              setAvatarUrl={setAvatarUrl}
            />
          ) : (
            <UserAvatar
              user={user}
              shouldClickRedirect={false}
              className="user-form-avatar"
            />
          )
        }
        honorific={
          isEdit ? (
            <FormFieldWrapper label={t("profiles.honorific")} required>
              <HookFormBaseDropdown
                name="profile.honorific"
                placeholder={t("profiles.honorific")}
                options={Honorifics.asList()}
                control={form.control}
              />
            </FormFieldWrapper>
          ) : (
            Honorifics.getLabel(user.profile.honorific)
          )
        }
        firstName={
          isEdit ? (
            <FormFieldWrapper label={t("profiles.firstName")} required>
              <HookFormInput
                name="firstName"
                placeholder={t("profiles.firstName")}
                control={form.control}
              />
            </FormFieldWrapper>
          ) : (
            user.firstName
          )
        }
        lastName={
          isEdit ? (
            <FormFieldWrapper label={t("profiles.lastName")} required>
              <HookFormInput
                name="lastName"
                placeholder={t("profiles.lastName")}
                control={form.control}
              />
            </FormFieldWrapper>
          ) : (
            user.lastName
          )
        }
        jobTitle={
          isEdit ? (
            <FormFieldWrapper label={t("profiles.jobTitle")} required>
              <HookFormInput
                name="profile.jobTitle"
                placeholder={t("profiles.jobTitle")}
                control={form.control}
              />
            </FormFieldWrapper>
          ) : (
            user.profile.jobTitle
          )
        }
        department={
          isEdit ? (
            <FormFieldWrapper label={t("profiles.department")} required>
              <HookFormInput
                name="profile.department"
                placeholder={t("profiles.department")}
                control={form.control}
              />
            </FormFieldWrapper>
          ) : (
            user.profile.department
          )
        }
        email={isEdit ? undefined : user.email}
        city={
          isEdit ? (
            <FormFieldWrapper label={t("profiles.city")} required>
              <HookFormInput
                name="profile.city"
                placeholder={t("profiles.city")}
                control={form.control}
              />
            </FormFieldWrapper>
          ) : (
            user.profile.city
          )
        }
        country={
          isEdit ? (
            <FormFieldWrapper label={t("profiles.country")} required>
              <HookFormBaseDropdown
                name="profile.country"
                placeholder={t("profiles.country")}
                options={countries}
                control={form.control}
              />
            </FormFieldWrapper>
          ) : countriesData ? (
            countriesData[user.profile.country]
          ) : null
        }
        role={isEdit ? null : OrganizationRoles.getLabel(user.organizationRole)}
        showLabels={!isEdit}
        primaryButtonLabel={
          isEdit ? t("buttons.save") : t("buttons.editProfile")
        }
        onPrimaryButtonClick={
          isEdit ? form.handleSubmit(onSubmit) : () => setIsEdit(true)
        }
        primaryButtonIcon={isEdit ? <SaveOutlined /> : <EditOutlined />}
        onSecondaryButtonClick={isEdit ? handleCancel : undefined}
        secondaryButtonLabel={isEdit ? t("buttons.cancel") : undefined}
      />
    )
  );
};

export default UserForm;
